import { Routes } from '@angular/router';
import { authGuard, redirectIfAuthenticated } from './guards/auth.guard';
import { locationGuard } from './guards/location.guard';
import { seoTitleResolver } from './guards/seo-title.resolver';
import { accountSetupDeactivateGuard } from './guards/account-setup-deactivate.guard';

export const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    redirectTo: 'tabs/home',
  },
  {
    path: 'test-webview',
    loadComponent: () => import('./pages/test-webview/test-webview.page').then(m => m.TestWebViewPage),
  },
  {
    path: 'login',
    loadComponent: () => import('./pages/auth/login.page').then(m => m.LoginPage),
    canActivate: [redirectIfAuthenticated],
    resolve: { seo: seoTitleResolver },
    data: {
      seo: {
        titleKey: 'seo.login',
        title: 'Sign In',
      },
    },
  },
  // Registration disabled - users must be created via admin portal
  // {
  //   path: 'register',
  //   loadComponent: () => import('./pages/auth/register.page').then(m => m.RegisterPage),
  //   canActivate: [redirectIfAuthenticated],
  // },
  {
    path: 'account-setup',
    loadComponent: () => import('./pages/account-setup/account-setup.page').then(m => m.AccountSetupPage),
    canActivate: [authGuard],
    canDeactivate: [accountSetupDeactivateGuard],
    resolve: { seo: seoTitleResolver },
    data: {
      seo: {
        titleKey: 'seo.accountSetup',
        title: 'Account Setup',
      },
    },
  },
  {
    path: 'vendor/:vendorId',
    loadComponent: () => import('./pages/vendor/vendor-detail.page').then(m => m.VendorDetailPage),
    canActivate: [authGuard],
    resolve: { seo: seoTitleResolver },
    data: {
      seo: {
        titleKey: 'seo.vendor',
        title: 'Restaurant Details',
        description: 'View restaurant menu, ratings, and reviews. Order food online for delivery or pickup.',
        keywords: 'restaurant, menu, food delivery, restaurant details',
      },
    },
  },
  {
    path: 'location',
    loadComponent: () => import('./pages/location/location.page').then((m) => m.LocationPage),
    canActivate: [authGuard],
    resolve: { seo: seoTitleResolver },
    data: {
      seo: {
        titleKey: 'seo.location',
        title: 'Your Address',
      },
    },
  },
  {
    path: 'tabs',
    loadComponent: () => import('./pages/tabs/tabs.page').then(m => m.TabsPage),
    canActivate: [authGuard],
    children: [
      {
        path: 'home',
        loadComponent: () => import('./pages/home/home.page').then(m => m.HomePage),
        canActivate: [locationGuard],
        resolve: { seo: seoTitleResolver },
        data: {
          seo: {
            titleKey: 'seo.home',
            title: 'Home - Order Food Online',
            description: 'Browse restaurants and order food online. Fast delivery, great prices, and a wide selection of cuisines.',
            keywords: 'food delivery, order food online, restaurants, food app',
          },
        },
      },
      {
        path: 'categories',
        loadComponent: () => import('./pages/categories/categories.page').then(m => m.CategoriesPage),
        resolve: { seo: seoTitleResolver },
        data: {
          seo: {
            titleKey: 'seo.categories',
            title: 'Food Categories',
            description: 'Browse food by category - Pizza, Burgers, Indian, Chinese, and more. Find your favorite cuisine.',
            keywords: 'food categories, cuisine types, restaurant categories',
          },
        },
      },
      {
        path: 'categories/:categoryId/products',
        loadComponent: () => import('./pages/products/product-list.page').then(m => m.ProductListPage),
      },
      {
        path: 'local-gems',
        loadComponent: () => import('./pages/local-gems/local-gems.page').then(m => m.LocalGemsPage),
        resolve: { seo: seoTitleResolver },
        data: {
          seo: {
            titleKey: 'seo.localGems',
            title: 'Local Gems - Discover Hidden Gems',
            description: 'Explore local gems and hidden favorites in your area. Discover unique restaurants and food vendors.',
            keywords: 'local gems, local restaurants, hidden gems, local favorites',
          },
        },
      },
      {
        path: 'recently-bought',
        loadComponent: () =>
          import('./pages/recently-bought/recently-bought.page').then((m) => m.RecentlyBoughtPage),
        resolve: { seo: seoTitleResolver },
        data: {
          seo: {
            title: 'Recently Bought',
            description: 'Reorder products you bought before. Browse your recent purchases and add them to cart again.',
            keywords: 'recently bought, past purchases, reorder, buy again',
          },
        },
      },
      {
        path: 'restaurants',
        loadComponent: () => import('./pages/restaurants/restaurants.page').then(m => m.RestaurantsPage),
        resolve: { seo: seoTitleResolver },
        data: {
          seo: {
            titleKey: 'seo.restaurants',
            title: 'Nearby Stores - All Vendors',
            description: 'Browse all nearby stores including restaurants, bakeries, groceries, and more. Find your favorite vendors and order online.',
            keywords: 'nearby stores, restaurants, bakeries, groceries, food stores, vendors, order online',
          },
        },
      },
      {
        path: 'pickup',
        loadComponent: () => import('./pages/pickup/pickup.page').then(m => m.PickupPage),
        resolve: { seo: seoTitleResolver },
        data: {
          seo: {
            titleKey: 'seo.pickup',
            title: 'Pickup Orders',
            description: 'Order food for pickup from nearby restaurants. Skip the delivery fee and pick up your order.',
            keywords: 'food pickup, order pickup, restaurant pickup',
          },
        },
      },
      {
        path: 'search',
        loadComponent: () => import('./pages/search/search.page').then(m => m.SearchPage),
        resolve: { seo: seoTitleResolver },
        data: {
          seo: {
            titleKey: 'seo.search',
            title: 'Browse Stores',
            description: 'Search for restaurants, cuisines, and food items. Find exactly what you\'re craving.',
            keywords: 'search restaurants, find food, restaurant search',
          },
        },
      },
      {
        path: 'orders',
        loadComponent: () => import('./pages/orders/orders.page').then(m => m.OrdersPage),
        canActivate: [authGuard],
        resolve: { seo: seoTitleResolver },
        data: {
          seo: {
            titleKey: 'seo.orders',
            title: 'Your Orders',
          },
        },
      },
      {
        path: 'orders/track/:orderId',
        loadComponent: () => import('./pages/orders/order-tracking.page').then(m => m.OrderTrackingPage),
        canActivate: [authGuard],
      },
      {
        path: 'orders/success/:orderId',
        loadComponent: () => import('./pages/orders/order-success.page').then(m => m.OrderSuccessPage),
        canActivate: [authGuard],
      },
      {
        path: 'orders/invoice/:orderId',
        loadComponent: () => import('./pages/orders/order-invoice.page').then(m => m.OrderInvoicePage),
        canActivate: [authGuard],
        resolve: { seo: seoTitleResolver },
        data: {
          seo: {
            titleKey: 'seo.invoice',
            title: 'Order Invoice',
          },
        },
      },
      {
        path: 'account',
        loadComponent: () => import('./pages/account/account.page').then(m => m.AccountPage),
        canActivate: [authGuard],
        resolve: { seo: seoTitleResolver },
        data: {
          seo: {
            titleKey: 'seo.account',
            title: 'Your Account',
          },
        },
      },
      {
        path: 'cart',
        loadComponent: () => import('./pages/cart/cart.page').then(m => m.CartPage),
        canActivate: [authGuard],
        resolve: { seo: seoTitleResolver },
        data: {
          seo: {
            titleKey: 'seo.cart',
            title: 'Your Cart',
          },
        },
      },
      {
        path: 'notifications',
        loadComponent: () => import('./pages/notifications/notifications.page').then(m => m.NotificationsPage),
        canActivate: [authGuard],
        resolve: { seo: seoTitleResolver },
        data: {
          seo: {
            titleKey: 'seo.notifications',
            title: 'Notifications',
          },
        },
      },
      // Wallet system is not implemented yet: Commented out for security reasons and will be implemented later
      /* 
      {
        path: 'wallet',
        loadComponent: () => import('./pages/wallet/wallet.page').then(m => m.WalletPage),
        canActivate: [authGuard],
      },
      {
        path: 'wallet/add-money',
        loadComponent: () => import('./pages/wallet/add-money.page').then(m => m.AddMoneyPage),
        canActivate: [authGuard],
      },
      {
        path: 'wallet/withdraw',
        loadComponent: () => import('./pages/wallet/withdraw.page').then(m => m.WithdrawPage),
        canActivate: [authGuard],
      },
      */
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'home',
      },
    ],
  },
  {
    path: 'parcel/create',
    loadComponent: () => import('./pages/parcel/create-parcel.page').then(m => m.CreateParcelPage),
    canActivate: [authGuard],
  },
  {
    path: 'parcel/track/:id',
    loadComponent: () => import('./pages/parcel/track-parcel.page').then(m => m.TrackParcelPage),
    canActivate: [authGuard],
  },
  {
    path: 'parcel/payment/:id',
    loadComponent: () => import('./pages/parcel/parcel-payment.page').then(m => m.ParcelPaymentPage),
    canActivate: [authGuard],
  },
  {
    path: '**',
    redirectTo: 'tabs/home',
  },
];
